import React, { useEffect, useMemo, useState } from "react";
import {
  addCategory,
  listCategories,
  deleteCategory,
  updateCategory,
} from "../services/categoryService";
import { ToastContainer, toast } from "react-toastify";

// ✅ Firebase Storage (assume que tu as déjà firebase initialisé dans ../services/firebase)
import { storage, auth } from "../firebaseConfig";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

/* ---------- Helpers ---------- */
const safe = (v) => (typeof v === "string" ? v.trim() : "");

const slugify = (s) =>
  safe(s)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

function guessExt(file) {
  const name = file?.name || "";
  const m = name.match(/\.(\w+)$/);
  if (m?.[1]) return m[1].toLowerCase();
  const type = file?.type || "";
  if (type.includes("png")) return "png";
  if (type.includes("webp")) return "webp";
  if (type.includes("jpeg") || type.includes("jpg")) return "jpg";
  return "jpg";
}

export default function Categories() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ ajout imageUrl + imagePath
  const [form, setForm] = useState({
    name: "",
    slug: "",
    parentId: "",
    imageUrl: "",
    imagePath: "",
  });

  const [editing, setEditing] = useState(null);

  // ✅ état upload
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = async (reset = false) => {
    setLoading(true);
    try {
      const res = await listCategories({
        pageSize: 50,
        cursor: reset ? null : cursor,
      });
      if (reset) setItems(res.items);
      else setItems((prev) => [...prev, ...res.items]);
      setCursor(res.nextCursor);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parents = useMemo(() => items.filter((i) => !i.parentId), [items]);

  const resetForm = () => {
    setForm({ name: "", slug: "", parentId: "", imageUrl: "", imagePath: "" });
    setEditing(null);
    setFile(null);
    setPreview("");
  };

  // ✅ gestion preview local
  const onPickFile = (f) => {
    if (!f) {
      setFile(null);
      setPreview("");
      return;
    }
    if (!f.type?.startsWith("image/")) {
      toast.error("Veuillez choisir une image");
      return;
    }
    if (f.size > 4 * 1024 * 1024) {
      toast.error("Image trop lourde (max 4MB)");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  // ✅ upload image dans Storage + retour {url, path}
  const uploadCategoryImage = async ({ file, name, slug }) => {
    const ext = guessExt(file);
    const safeSlug = slugify(slug || name || "categorie");
    const path = `categories/${safeSlug}-${Date.now()}.${ext}`;

    const storageRef = ref(storage, path);
    console.log("UPLOAD UID:", auth.currentUser?.uid, "EMAIL:", auth.currentUser?.email);
    await uploadBytes(storageRef, file, {
      contentType: file.type || "image/jpeg",
      cacheControl: "public,max-age=31536000",
    });

    const url = await getDownloadURL(storageRef);
    return { url, path };
  };

  const tryDeleteOldImage = async (path) => {
    try {
      if (!path) return;
      await deleteObject(ref(storage, path));
    } catch {
      // ok: si fichier n'existe plus / droits insuffisants
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      // slug auto si vide
      const computedSlug = safe(form.slug) ? safe(form.slug) : slugify(form.name);

      let imageUrl = form.imageUrl || "";
      let imagePath = form.imagePath || "";

      // ✅ si nouveau fichier choisi → upload
      if (file) {
        const up = await uploadCategoryImage({
          file,
          name: form.name,
          slug: computedSlug,
        });

        // si on remplace une ancienne image (édition) → supprimer l’ancienne
        if (editing?.imagePath && editing.imagePath !== up.path) {
          await tryDeleteOldImage(editing.imagePath);
        }

        imageUrl = up.url;
        imagePath = up.path;
      }

      const payload = {
        name: safe(form.name),
        slug: computedSlug,
        parentId: form.parentId || null,
        imageUrl,
        imagePath,
        updatedAt: Date.now(),
      };

      if (editing) {
        await updateCategory(editing.id, payload);
        toast.success("Catégorie mise à jour");
      } else {
        await addCategory({
          ...payload,
          createdAt: Date.now(),
        });
        toast.success("Catégorie ajoutée");
      }

      resetForm();
      load(true);
    } catch (err) {
      toast.error(err?.message || "Erreur d'enregistrement");
    } finally {
      setUploading(false);
    }
  };

  const onEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      slug: c.slug || "",
      parentId: c.parentId || "",
      imageUrl: c.imageUrl || "",
      imagePath: c.imagePath || "",
    });
    setFile(null);
    setPreview(c.imageUrl || "");
  };

  const onRemoveImage = async () => {
    // ✅ si on a une image déjà enregistrée sur la catégorie en cours d’édition
    if (editing?.imagePath) {
      if (!window.confirm("Supprimer l'image de cette catégorie ?")) return;
      try {
        setUploading(true);
        await tryDeleteOldImage(editing.imagePath);
        await updateCategory(editing.id, {
          imageUrl: "",
          imagePath: "",
          updatedAt: Date.now(),
        });
        toast.success("Image supprimée");
        setForm((f) => ({ ...f, imageUrl: "", imagePath: "" }));
        setPreview("");
        setFile(null);
        load(true);
      } catch (e) {
        toast.error(e?.message || "Erreur suppression image");
      } finally {
        setUploading(false);
      }
      return;
    }

    // ✅ si c’est juste un preview local (pas encore sauvegardé)
    setFile(null);
    setPreview("");
    setForm((f) => ({ ...f, imageUrl: "", imagePath: "" }));
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cette catégorie ?")) return;

    try {
      const cat = items.find((i) => i.id === id);

      // ✅ supprimer image storage si existante
      if (cat?.imagePath) {
        await tryDeleteOldImage(cat.imagePath);
      }

      await deleteCategory(id);
      toast.success("Supprimé");
      setItems(items.filter((i) => i.id !== id));
    } catch (e) {
      toast.error(e?.message || "Erreur suppression");
    }
  };

  return (
    <div className="container py-4">
      <ToastContainer position="top-right" />

      <div className="row g-4">
        {/* FORM */}
        <div className="col-lg-5">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="m-0">
                  {editing ? "Modifier la catégorie" : "Ajouter une catégorie"}
                </h5>
                {editing ? (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    type="button"
                    onClick={resetForm}
                    disabled={uploading}
                  >
                    Annuler
                  </button>
                ) : null}
              </div>

              <form onSubmit={onSubmit}>
                <div className="mb-3">
                  <label className="form-label">Nom</label>
                  <input
                    className="form-control"
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                    required
                    disabled={uploading}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Slug (auto si vide)</label>
                  <input
                    className="form-control"
                    value={form.slug}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, slug: e.target.value }))
                    }
                    placeholder={slugify(form.name) || "ex: chaussures-homme"}
                    disabled={uploading}
                  />
                  <div className="form-text">
                    Recommandé: <code>{slugify(form.slug || form.name) || "categorie"}</code>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Parent (optionnel)</label>
                  <select
                    className="form-select"
                    value={form.parentId}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, parentId: e.target.value }))
                    }
                    disabled={uploading}
                  >
                    <option value="">— Aucun —</option>
                    {parents.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ✅ Upload image */}
                <div className="mb-3">
                  <label className="form-label">Image représentative</label>

                  <div className="d-flex gap-3 align-items-start flex-wrap">
                    <div
                      className="border rounded-3 overflow-hidden bg-light"
                      style={{ width: 120, height: 120 }}
                    >
                      {preview ? (
                        <img
                          src={preview}
                          alt="Aperçu"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center text-muted small"
                          style={{ width: "100%", height: "100%" }}
                        >
                          Aucun
                        </div>
                      )}
                    </div>

                    <div className="flex-grow-1" style={{ minWidth: 220 }}>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={(e) => onPickFile(e.target.files?.[0])}
                        disabled={uploading}
                      />
                      <div className="form-text">
                        JPG/PNG/WebP • max 4MB. (Upload lors de l’enregistrement)
                      </div>

                      <div className="d-flex gap-2 mt-2">
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={onRemoveImage}
                          disabled={uploading || (!preview && !form.imageUrl)}
                        >
                          Retirer l'image
                        </button>

                        {editing?.imageUrl ? (
                          <a
                            href={editing.imageUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-outline-secondary btn-sm"
                          >
                            Ouvrir
                          </a>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-grid">
                  <button className="btn btn-success" disabled={uploading}>
                    {uploading
                      ? "Enregistrement..."
                      : editing
                      ? "Mettre à jour"
                      : "Ajouter"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* LIST */}
        <div className="col-lg-7">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="m-0">Catégories</h5>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => load(true)}
                  disabled={loading || uploading}
                >
                  Rafraîchir
                </button>
              </div>

              <div className="table-responsive">
                <table className="table align-middle table-hover">
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>Image</th>
                      <th>Nom</th>
                      <th>Slug</th>
                      <th>Parent</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <div
                            className="border rounded-3 overflow-hidden bg-light"
                            style={{ width: 52, height: 52 }}
                          >
                            {c.imageUrl ? (
                              <img
                                src={c.imageUrl}
                                alt={c.name}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                                loading="lazy"
                              />
                            ) : (
                              <div
                                className="d-flex align-items-center justify-content-center text-muted"
                                style={{ width: "100%", height: "100%", fontSize: 12 }}
                              >
                                —
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="fw-semibold">{c.name}</td>
                        <td className="text-muted small">{c.slug}</td>
                        <td className="text-muted small">
                          {items.find((i) => i.id === c.parentId)?.name || "—"}
                        </td>
                        <td className="text-end">
                          <div className="btn-group">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => onEdit(c)}
                              disabled={uploading}
                            >
                              Éditer
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => onDelete(c.id)}
                              disabled={uploading}
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-grid">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => load(false)}
                  disabled={!cursor || loading || uploading}
                >
                  {loading ? "Chargement..." : cursor ? "Charger plus" : "Fin de liste"}
                </button>
              </div>
            </div>
          </div>

          {/* Petit hint */}
          <div className="text-muted small mt-2">
            Astuce: si tu remplaces l’image d’une catégorie, l’ancienne est supprimée du Storage.
          </div>
        </div>
      </div>
    </div>
  );
}
